import { Icon, Modal, Tooltip } from "@shopify/polaris"
import { SettingsMinor } from "@shopify/polaris-icons"
import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import NegativeComparisonIndicator from "../../assets/NegativeComparisonIndicator.svg"
import PositiveComparisonIndicator from "../../assets/PositiveComparisonIndicator.svg"
import WarningComparisonIndicator from "../../assets/WarningComparisonIndicator.svg"
import "../../assets/countryCard.css"
import "../../assets/neumorphism.css"
import { useUpdateFeatureSetting } from "../../hooks/useProtectHooks.js"
import { protectFeatures } from "../../utils/protectFeatures.jsx"
import { ProtectStateContext } from "../providers/ProtectStateProvider.jsx"
import CountryBlockerSettings from "./CountryBlockerSettings"
import GeolocationRedirectSettings from "./GeolocationRedirectSettings.jsx"

export const ProtectFeatureCard = ({ settingId }) => {
	const { protectState, setProtectState } = useContext(ProtectStateContext)
	const [t] = useTranslation()
	const updateFeatureSetting = useUpdateFeatureSetting()

	const setting = protectState.featureSettings?.[settingId]

	const isFeatureBoolean = typeof setting === "boolean"
	const enabled = isFeatureBoolean ? setting : setting?.enabled || false

	const [countriesModalOpen, setCountriesModalOpen] = useState(false)
	const [geolocationRedirectModalOpen, setGeolocationRedirectModalOpen] =
		useState(false)

	const [hoveringButton, setHoveringButton] = useState(false)

	const needsConfiguration =
		(settingId === "block_countries" &&
			!setting?.countriesBlocked?.length) ||
		(settingId === "geolocation_redirect" && !setting?.redirects?.length)
	// || settingId === "sales_deceiver" && setting.salesDeceiverRequiresConfiguration

	const handleOpenCountriesModal = () => {
		setCountriesModalOpen(true)
	}

	const handleCloseCountriesModal = () => {
		setCountriesModalOpen(false)
	}

	const handleOpenGeolocationRedirectModal = () => {
		setGeolocationRedirectModalOpen(true)
	}

	const handleCloseGeolocationRedirectModal = () => {
		setGeolocationRedirectModalOpen(false)
	}

	const saveCountryBlockerSettings = async ({
		countriesBlocked,
		blockedPageImg,
		ipWhitelist,
	}) => {
		setProtectState({
			...protectState,
			featureSettings: {
				...protectState.featureSettings,
				[settingId]: {
					enabled,
					countriesBlocked,
					blockedPageImg,
					ipWhitelist,
				},
			},
		})
		setCountriesModalOpen(false)

		updateFeatureSetting({
			[settingId]: {
				enabled,
				countriesBlocked,
				blockedPageImg,
				ipWhitelist,
			},
		})
	}

	const saveGeolocationRedirectSettings = async ({ redirects }) => {
		setProtectState({
			...protectState,
			featureSettings: {
				...protectState.featureSettings,
				[settingId]: { enabled, redirects },
			},
		})
		setGeolocationRedirectModalOpen(false)

		updateFeatureSetting({ [settingId]: { enabled, redirects } })
	}

	const toggleSetting = () => {
		if (typeof setting === "boolean") {
			setProtectState({
				...protectState,
				featureSettings: {
					...protectState.featureSettings,
					[settingId]: !enabled,
				},
			})

			updateFeatureSetting({ [settingId]: !enabled })
		} else {
			setProtectState({
				...protectState,
				featureSettings: {
					...protectState.featureSettings,
					[settingId]: { ...setting, enabled: !enabled },
				},
			})

			updateFeatureSetting({
				[settingId]: { ...setting, enabled: !enabled },
			})
		}
	}

	const icon = protectFeatures[settingId].icon
	const isKopyShieldCard = settingId === "kopy_shield"
	const isNew = protectFeatures[settingId].new || false

	return (
		<div
			className="neu-background shadow"
			style={{
				position: "relative",
				height: "220px",
				width: "310px",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				padding: "1rem",
			}}
		>
			<div
				style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
			>
				<div style={{ width: "1.8rem", height: "1.8rem" }}>{icon}</div>

				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "0.3rem",
						maxWidth: "79%",
					}}
				>
					<Tooltip
						content={
							needsConfiguration && enabled ? (
								<div
									style={{
										padding: "0.3rem",
										fontSize: "0.8rem",
									}}
								>
									{t("protect.needs_further_configuration")}
								</div>
							) : (
								""
							)
						}
					>
						<h1
							style={{
								fontSize: "1rem",
								lineHeight: "1.1",
								fontWeight: "600",
							}}
						>
							{t(`protect.feature_card.${settingId}.name`)}
							{needsConfiguration && enabled && " ⚠️"}
						</h1>
					</Tooltip>
				</div>

				{isNew ? (
					<div
						style={{
							padding: "0.15rem 0.45rem",
							background: "#444",
							borderRadius: "0.5rem",
							fontSize: "0.65rem",
							color: "#fff",
							fontWeight: "600",
						}}
					>
						{t("common.new")}
					</div>
				) : (
					""
				)}
			</div>

			<div
				className="font-satoshi"
				style={{
					fontSize: "0.8rem",
					color: "#777",
					lineHeight: "16px",
				}}
			>
				{t(`protect.feature_card.${settingId}.description`)}
			</div>

			<div style={{ display: "flex", gap: "0.5rem" }}>
				<button
					className="neu-background shadow neu-button"
					style={{ flex: "1" }}
					onClick={toggleSetting}
					onMouseEnter={() => setHoveringButton(true)}
					onMouseLeave={() => setHoveringButton(false)}
				>
					<span style={{ fontWeight: "600" }}>
						{enabled
							? t("protect.feature_card.disable")
							: t("protect.feature_card.enable")}
					</span>
				</button>

				{settingId == "block_countries" && (
					<button
						className="neu-background shadow neu-button"
						style={{
							padding: "0.6rem 0.7rem",
							position: "relative",
						}}
						onClick={handleOpenCountriesModal}
					>
						<Icon source={SettingsMinor} color="subdued" />
					</button>
				)}

				{settingId == "geolocation_redirect" && (
					<button
						className="neu-background shadow neu-button"
						style={{
							padding: "0.6rem 0.7rem",
							position: "relative",
						}}
						onClick={handleOpenGeolocationRedirectModal}
					>
						<Icon source={SettingsMinor} color="subdued" />
					</button>
				)}
			</div>

			{settingId === "block_countries" && (
				<Modal
					title={
						<span style={{ fontSize: "1.5rem", fontWeight: "600" }}>
							{t("protect.block_countries.header")}
						</span>
					}
					open={countriesModalOpen}
					onClose={handleCloseCountriesModal}
				>
					<CountryBlockerSettings
						countriesBlocked={setting?.countriesBlocked || []}
						blockedPageImg={setting?.blockedPageImg || null}
						ipWhitelist={setting?.ipWhitelist || []}
						onSaveSettings={saveCountryBlockerSettings}
					/>
				</Modal>
			)}

			{settingId === "geolocation_redirect" && (
				<Modal
					title={
						<span style={{ fontSize: "1.5rem", fontWeight: "600" }}>
							{t(
								"protect.geolocation_redirect.geolocation_redirect_settings",
							)}
						</span>
					}
					open={geolocationRedirectModalOpen}
					onClose={handleCloseGeolocationRedirectModal}
				>
					<GeolocationRedirectSettings
						redirects={setting?.redirects || []}
						onSaveSettings={saveGeolocationRedirectSettings}
					/>
				</Modal>
			)}

			<img
				className={
					enabled
						? needsConfiguration
							? "pulse-yellow"
							: "pulse-green"
						: "pulse-red"
				}
				style={{
					width: "16px",
					height: "16px",
					position: "absolute",
					top: "1rem",
					right: "1rem",
				}}
				src={
					enabled
						? needsConfiguration
							? WarningComparisonIndicator
							: PositiveComparisonIndicator
						: NegativeComparisonIndicator
				}
			/>
		</div>
	)
}
